import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";

// Same minimum as the backend's GET /friends/search.
const SEARCH_MIN_LETTERS = 3;

const AddFriendDialog = ({ open, onClose, user, onSuccess }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const value = text.trim();
  const isEmail = value.includes("@");
  const canSearch = !isEmail && value.replace(/\s/g, "").length >= SEARCH_MIN_LETTERS;

  // Search by name as the user types (debounced); an email is invited directly.
  useEffect(() => {
    if (!open || !canSearch) {
      setResults(null);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    // Results of the previous query must not stay clickable while this one runs.
    setResults(null);
    setError(null);
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const idToken = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/friends/search?q=${encodeURIComponent(value)}`,
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || "Nie udało się wyszukać");
        }
        const data = await response.json();
        if (!cancelled) setResults(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, canSearch, value, user]);

  const invite = async (key, body) => {
    setLoading(key);
    setError(null);

    try {
      const idToken = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/friends/invite`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Nie udało się wysłać zaproszenia");
      }

      setText("");
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
    }
  };

  const handleSubmit = () => {
    if (isEmail) invite("email", { email: value });
  };

  const handleClose = () => {
    setText("");
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Dodaj znajomego</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          label="Imię i nazwisko lub adres email"
          type="text"
          fullWidth
          variant="outlined"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
          }}
          disabled={!!loading}
        />
        {value && !isEmail && !canSearch && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Wpisz co najmniej {SEARCH_MIN_LETTERS} litery imienia lub nazwiska.
          </Typography>
        )}
        {canSearch && searching && !results && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Szukam…
          </Typography>
        )}
        {canSearch && results && results.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Nie znaleźliśmy nikogo o tym imieniu i nazwisku. Możesz też wpisać adres email znajomego.
          </Typography>
        )}
        {canSearch && results && results.length > 0 && (
          <List dense sx={{ mt: 1 }}>
            {results.map((found) => (
              <ListItem
                key={found.id}
                data-testid="friend-search-result"
                disableGutters
                secondaryAction={
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!!loading}
                    onClick={() => invite(`user-${found.id}`, { user_id: found.id })}
                  >
                    {loading === `user-${found.id}` ? <CircularProgress size={18} /> : "Zaproś"}
                  </Button>
                }
              >
                <ListItemText primary={found.full_name} secondary={found.masked_email} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={!!loading}>
          Anuluj
        </Button>
        {isEmail && (
          <Button onClick={handleSubmit} variant="contained" disabled={!!loading}>
            {loading === "email" ? <CircularProgress size={20} /> : "Wyślij zaproszenie"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddFriendDialog;
